<template>
  <div
    :disabled="$props.disabled"
    :class="`position-relative ${$props.disabled ? 'disabled' : ''}`"
  >
    <div
      id="map"
      ref="map"
      @mouseout="onMouseOut"
    />

    <div
      id="extras"
      class="bg-white p-2"
      v-show="(selected && selected.length > 0) || (supplementaryLayers.length > 0 && supplementaryLayerOptions)"
    >
      <p
        class="small m-0"
        v-show="selected && selected.length > 0"
      >
        Selected: {{ selected }}
      </p>
      <hr class="my-2">
      <div v-if="supplementaryLayers.length > 0 && supplementaryLayerOptions">
        <p class="mb-2 text-muted small">
          Toggle Extra Layers
        </p>
        <button
          v-for="layer in supplementaryLayers"
          :key="layer.id"
          class="btn btn-link p-0 btn-sm mb-2 d-block"
          type="button"
          @click="() => toggleSupplementaryLayer(layer.id)"
        >
          <template v-if="supplementaryLayerOptions.find(l => l.id === layer.id ).visible">
            Hide
          </template>
          <template v-else>
            Show
          </template>
          {{ layer.id }}
        </button>
      </div>
    </div>

    <div class="form-row mt-2 mb-2">
      <div class="col col-12 col-lg-6">
        <button
          type="button"
          class="btn btn-sm btn-outline-primary w-100"
          :disabled="$props.disabled || loading || disableWKT"
          @click="triggerWKTUpload"
        >
          Upload WKT
        </button>
      </div>
      <div class="col col-12 col-lg-6">
        <button
          type="button"
          class="btn btn-sm btn-outline-primary w-100"
          :disabled="$props.disabled || loading || disableGeoJSON"
          @click="triggerGeoJsonUpload"
        >
          Upload Geo JSON
        </button>
      </div>
    </div>

    <div
      v-if="showWKTUpload"
      class="row mb-2"
    >
      <div class="col col-12">
        <div class="form-group m-0">
          <textarea
            :class="`form-control ${wktError !== null ? 'is-invalid' : ''}`"
            rows="1"
            placeholder="POLYGON ((30 10, 40 40, 20 40, 10 20, 30 10))"
            v-model="wktInput"
          />
          <div
            v-if="wktError"
            class="invalid-feedback font-weight-bold"
          >
            {{ wktError }}
          </div>
        </div>
        <div class="form-row">
          <div class="col-12 col-md-6">
            <button
              type="button"
              class="btn btn-sm btn-outline-success mt-2 w-100"
              @click="uploadWKT"
            >
              Upload WKT
            </button>
          </div>
          <div class="col-12 col-md-6">
            <button
              type="button"
              class="btn btn-sm btn-outline-danger mt-2 w-100"
              @click="cancelManualUpload"
            >
              Cancel Upload
            </button>
          </div>
        </div>
        <hr class="mt-2 mb-1">
      </div>
    </div>
    <div
      v-if="showGeoJSONUpload"
      class="row mb-2"
    >
      <div class="col col-12">
        <div class="form-group m-0">
          <textarea
            :class="`form-control ${geoJSONError !== null ? 'is-invalid' : ''}`"
            rows="1"
            :placeholder="geoJSONExample"
            v-model="geoJSONInput"
            @input="onGeoJSONInput"
          />
          <div
            v-if="geoJSONError"
            class="invalid-feedback font-weight-bold"
          >
            {{ geoJSONError }}
          </div>
        </div>
        <div class="form-row">
          <div class="col-12 col-md-6">
            <button
              type="button"
              class="btn btn-sm btn-outline-danger mt-2 w-100"
              @click="cancelManualUpload"
            >
              Cancel Upload
            </button>
          </div>
          <div class="col-12 col-md-6">
            <button
              type="button"
              class="btn btn-sm btn-outline-success mt-2 w-100"
              @click="uploadGeoJSON"
            >
              Upload Geo JSON
            </button>
          </div>
        </div>
        <hr class="mt-2 mb-1">
      </div>
    </div>

    <div class="form-row mt-2">
      <div
        class="col col-12 col-lg-4"
      >
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary w-100"
          :disabled="$props.disabled || loading || disableGeoJSON || disableViewWKT"
          @click="viewWKT"
        >
          View as WKT
        </button>
      </div>
      <div
        class="col col-12 col-lg-4"
      >
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary w-100"
          :disabled="$props.disabled || loading || disableGeoJSON || disableViewGeoJSON"
          @click="viewGeoJSON"
        >
          View as Geo JSON
        </button>
      </div>
      <div
        class="col col-12 col-lg-4"
      >
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary w-100"
          :disabled="$props.disabled || loading || disableGeoJSON || disableWKT"
          @click="reload"
        >
          Reset
        </button>
      </div>
    </div>

    <div
      v-if="showFeatureViewer"
      class="form-row"
    >
      <div class="col col-12">
        <textarea
          class="form-control mb-2"
          rows="5"
          :value="featureAsString"
          readonly
        />
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary w-100 mb-2"
          @click="hideFeatureViewer"
        >
          Hide
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { getBoundingBoxFromFlatCoords, getBBOXCenter, withAdjacentDuplicatesRemoved } from '../../spatial';
import wkt from 'terraformer-wkt-parser';
import Terraformer from 'terraformer';

const geoJSONExample = `{
  "type": "Feature",
  "geometry": {
    "type": "Polygon",
    "coordinates": [
        [ [100.0, 0.0], [101.0, 0.0], [101.0, 1.0],
        [100.0, 1.0], [100.0, 0.0] ]
    ]
  }
}`;

export default {
    data() {
        return {
            initialised: false,
            loaded: false,
            map: null,
            draw: null,
            centerMarker: null,
            centerLatitude: null,
            centerLongitude: null,
            feature: null,
            bbox: null,
            loading: false,
            showWKTUpload: false,
            disableWKT: false,
            showGeoJSONUpload: false,
            disableGeoJSON: false,
            wktInput: null,
            geoJSONInput: null,
            geoJSONError: null,
            wktError: null,
            extractionURL: null,
            extractError: null,
            showFeatureViewer: false,
            disableViewGeoJSON: false,
            disableViewWKT: false,
            viewType: '',
            supplementaryLayerOptions: [],
            selected: '',
            hadChangesMade: false
        };
    },
    computed: {
        geoJSONExample() {
            return geoJSONExample;
        },
        featureAsWKT() {
            if ( !this.feature ) {
                return '';
            }
            return wkt.convert( {
                type: this.feature.features[ 0 ].geometry.type,
                coordinates: [ withAdjacentDuplicatesRemoved( this.feature.features[ 0 ].geometry.coordinates[ 0 ] ) ]
            } );
        },
        featureAsGeoJSON() {
            if ( !this.feature ) {
                return '';
            }
            return this.feature;
        },
        featureAsString() {
            if ( this.viewType === 'wkt' ) {
                return this.featureAsWKT;
            }
            return this.featureAsGeoJSON;
        }
    },
    methods: {
        initMap() {
            if ( this.initialised ) {
                return;
            }

            if ( this.map !== null ) {
                this.map.remove();
                this.map = null;
            }

            this.map = new mapboxgl.Map( {
                container: this.$refs.map,
                attributionControl: false,
                style: 'https://api.maptiler.com/maps/streets/style.json?key=orAg6xDhKXM7zeWeirOP',
                bounds: [
                    [ -14.595104766383656, 35.391245230422896 ],
                    [ 8.183799299894645, 44.143571373876114 ]
                ],
                fitBoundsOptions: {
                    padding: 20
                }
            } );

            this.draw = new MapboxDraw( {
                keybindings: false,
                touchEnabled: false,
                boxSelect: false,
                displayControlsDefault: true,
                controls: {
                    'polygon': true,
                    'line_string': false,
                    'point': false,
                    'combine_features': true,
                    'uncombine_features': false
                }
            } );

            this.map.on( 'load', this.onMapLoad.bind( this ) );
            this.map.on( 'draw.create', this.onDrawCreate.bind( this ) );
            this.map.on( 'draw.update', this.onDrawUpdate.bind( this ) );
            this.map.on( 'draw.delete', this.onDrawDelete.bind( this ) );
            this.map.on( 'draw.combine', this.onDrawCombine.bind( this ) );
            this.map.on( 'draw.selectionchange', this.onDrawSelectionChange.bind( this ) );

            this.initialised = true;
        },
        onMapLoad() {
            if ( this.loaded ) {
                return;
            }

            this.map.addControl( new mapboxgl.FullscreenControl(), 'top-right' );
            this.map.addControl( new mapboxgl.GeolocateControl(), 'top-right' );
            this.map.addControl( new mapboxgl.NavigationControl(), 'top-right' );
            this.map.addControl( this.draw, 'top-right' );

            if ( this.$props.initialLatitude !== null && this.$props.initialLongitude !== null ) {
                this.updateCenterMarker( this.$props.initialLatitude, this.$props.initialLongitude );
            }

            const addInitialGeometry = ( geojson ) => {
                this.draw.add( geojson );
                this.feature = this.draw.getAll();
                this.map.once( 'idle', () => {
                    this.updateBBOX();
                    this.focus();
                    this.emit();
                } );
            };

            if ( this.$props.initialGeometryAsGeoJson ) {
                addInitialGeometry( this.$props.initialGeometryAsGeoJson );
            } else if ( this.$props.initialGeometryAsWkt ) {
                const parsed = wkt.parse( this.$props.initialGeometryAsWkt );
                addInitialGeometry( parsed );
            }

            this.loaded = true;
        },
        reload() {
            this.initialised = false;
            this.hadChangesMade = true;
            this.loaded = false;
            this.removeCenterMarker();
            this.removeFeature();
            this.removeBBOX();
            this.cancelManualUpload();
            this.initMap();
        },
        emit() {
            const result = {
                wkt: this.featureAsWKT,
                geojson: this.featureAsGeoJSON,
                bbox: null,
                center: null,
                changed: this.hadChangesMade
            };

            if ( this.bbox !== null ) {
                result.bbox = this.bbox;
            }

            if ( this.centerLatitude && this.centerLongitude ) {
                result.center = {
                    latitude: this.centerLatitude,
                    longitude: this.centerLongitude
                };
            }

            this.$emit( 'update', result );
        },
        focus() {
            this.map.fitBounds( [
                [ this.bbox.southWest.lon, this.bbox.southWest.lat ],
                [ this.bbox.northEast.lon, this.bbox.northEast.lat ]
            ], { padding: 50 } );
        },
        onMouseOut( event ) {
            if ( !this.initialised || !this.loaded || !this.map || !this.draw || !event.relatedTarget ) {
                return;
            }
            if ( event.relatedTarget.closest( '#map' ) ) {
                return;
            }
            this.draw.changeMode( 'simple_select' );
            this.selected = '';
        },
        onDrawSelectionChange( event ) {
            if ( event.features.length === 0 ) {
                return;
            }

            const feature = event.features[ 0 ];

            if ( feature.properties && feature.properties[ 'LocationName' ] ) {
                this.selected = feature.properties[ 'LocationName' ];
            } else {
                this.selected = 'Unknown';
            }
        },
        onDrawCreate() {
            const feature = this.draw.getAll();

            if ( !feature || !feature.features ) {
                return;
            }

            this.feature = feature;

            this.updateBBOX();

            const center = getBBOXCenter( this.bbox );

            this.updateCenterMarker( center.lat, center.lon );

            this.map.once( 'idle', this.focus.bind( this ) );

            this.hadChangesMade = true;

            this.emit();
        },
        onDrawUpdate() {
            const feature = this.draw.getAll();

            if ( !feature || !feature.features ) {
                return;
            }

            this.feature = feature;

            this.updateBBOX();

            const center = getBBOXCenter( this.bbox );

            this.updateCenterMarker( center.lat, center.lon );

            this.hadChangesMade = true;

            this.emit();
        },
        onDrawDelete() {
            const feature = this.draw.getAll();

            if ( feature && feature.features.length > 0 ) {
                this.updateBBOX();

                const center = getBBOXCenter( this.bbox );

                this.updateCenterMarker( center.lat, center.lon );
                return;
            }

            this.feature = null;

            this.removeBBOX();

            this.removeCenterMarker();

            this.hadChangesMade = true;

            this.emit();
        },
        onDrawCombine() {
            const feature = this.draw.getAll();

            if ( !feature || !feature.features ) {
                return;
            }

            this.feature = feature;

            this.updateBBOX();

            const center = getBBOXCenter( this.bbox );

            this.updateCenterMarker( center.lat, center.lon );

            this.hadChangesMade = true;

            this.emit();
        },
        onMarkerMove() {
            const point = this.centerMarker.getLngLat();
            this.centerLatitude = point.lat;
            this.centerLongitude = point.lng;
            this.hadChangesMade = true;
            this.emit();
        },
        updateCenterMarker( lat, lon ) {
            if ( !this.centerMarker ) {
                this.centerMarker = new mapboxgl.Marker( {
                    color: '#d1041d',
                    draggable: true
                } );

                this.centerMarker.setLngLat( [ lon, lat ] );
                this.centerMarker.addTo( this.map );
                this.centerMarker.on( 'dragend', this.onMarkerMove.bind( this ) );
            }

            this.centerMarker.setLngLat( [ lon, lat ] );
            this.centerLatitude = lat;
            this.centerLongitude = lon;
        },
        updateBBOX() {
            if ( !this.feature ) {
                return;
            }

            let bounds = [];

            if ( this.feature.features.length === 1 ) {
                let coordinatesForBBOX = [];

                switch ( this.feature.features[ 0 ].geometry.type ) {
                case 'Polygon':
                    coordinatesForBBOX = this.feature.features[ 0 ].geometry.coordinates[ 0 ];
                    break;
                case 'MultiPolygon':
                    this.feature.features[ 0 ].geometry.coordinates.forEach( polygon => {
                        coordinatesForBBOX = [
                            ...coordinatesForBBOX,
                            ...polygon[ 0 ]
                        ];
                    } );
                }

                bounds = getBoundingBoxFromFlatCoords( coordinatesForBBOX );
            } else {
                const coordinatesForBBOX = this.feature.features.map( ( f ) => f.geometry.coordinates[ 0 ] ).reduce( ( a, c ) => [ ...a, ...c ], [] );
                bounds = getBoundingBoxFromFlatCoords( coordinatesForBBOX );
            }

            this.bbox = {
                northEast: {
                    lat: bounds[ 1 ][ 1 ],
                    lon: bounds[ 1 ][ 0 ]
                },
                southWest: {
                    lat: bounds[ 0 ][ 1 ],
                    lon: bounds[ 0 ][ 0 ]
                }
            };

            const coords = [ [
                [
                    bounds[ 0 ][ 0 ], // sw
                    bounds[ 0 ][ 1 ]
                ],
                [
                    bounds[ 1 ][ 0 ], // se
                    bounds[ 0 ][ 1 ]
                ],
                [
                    bounds[ 1 ][ 0 ], // ne
                    bounds[ 1 ][ 1 ]
                ],
                [
                    bounds[ 0 ][ 0 ], // nw
                    bounds[ 1 ][ 1 ]
                ],
                [
                    bounds[ 0 ][ 0 ], // sw
                    bounds[ 0 ][ 1 ]
                ]
            ] ];

            let src = this.map.getSource( 'bbox' );

            if ( !src ) {
                this.map.addSource( 'bbox', {
                    type: 'geojson',
                    data: {
                        type: 'Polygon',
                        coordinates: coords
                    },
                    tolerance: 0
                } );

                src = this.map.getSource( 'bbox' );
            }

            if ( !this.map.getLayer( 'bbox:outline' ) ) {
                this.map.addLayer( {
                    id: 'bbox:outline',
                    source: 'bbox',
                    type: 'line',
                    layout: {},
                    state: {},
                    paint: {
                        'line-color': '#4382CE',
                        'line-width': 1
                    }
                } );
            }

            src.setData( {
                type: 'Polygon',
                coordinates: coords
            } );

            if ( this.$props.initialLatitude === null && this.$props.initialLongitude === null ) {
                const bboxCenter = getBBOXCenter( this.bbox );
                this.updateCenterMarker( bboxCenter.lat, bboxCenter.lon );
            }
        },
        removeBBOX() {
            this.bbox = null;
            if ( this.map.getLayer( 'bbox:outline' ) ) {
                this.map.removeLayer( 'bbox:outline' );
            }
            if ( this.map.getSource( 'bbox' ) ) {
                this.map.removeSource( 'bbox' );
            }
        },
        removeFeature() {
            this.feature = null;
            this.draw.deleteAll();
        },
        removeCenterMarker() {
            this.centerLatitude = null;
            this.centerLongitude = null;
            this.centerMarker.remove();
            this.centerMarker = null;
        },
        triggerGeoJsonUpload() {
            this.showGeoJSONUpload = true;
            this.showWKTUpload = false;
            this.disableGeoJSON = true;
            this.disableWKT = true;
        },
        triggerWKTUpload() {
            this.showWKTUpload = true;
            this.showGeoJSONUpload = false;
            this.disableGeoJSON = true;
            this.disableWKT = true;
        },
        cancelManualUpload() {
            this.showWKTUpload = false;
            this.showGeoJSONUpload = false;
            this.disableGeoJSON = false;
            this.disableWKT = false;
            this.wktInput = null;
            this.geoJSONInput = null;
            this.wktError = null;
            this.geoJSONError = null;
        },
        uploadGeoJSON() {
            this.loading = true;
            this.wktError = null;

            const geoJSONStr = ( this.geoJSONInput || '' ).trim();

            if ( !geoJSONStr || geoJSONStr.length === 0 ) {
                this.geoJSONError = 'Enter a valid Geo JSON string';
                this.loading = false;
                return;
            }

            let parsedJSON;

            try {
                parsedJSON = JSON.parse( geoJSONStr );
            } catch ( err ) {
                this.geoJSONError = 'Failed to parse text as JSON';
                this.loading = false;
                return;
            }

            if ( parsedJSON[ 'geometry' ] ) {
                parsedJSON = parsedJSON[ 'geometry' ];
            }

            let parsedGeoJSON;

            try {
                parsedGeoJSON = new Terraformer.Primitive( parsedJSON );
            } catch ( err ) {
                this.geoJSONError = 'Failed to parse JSON as Geo JSON';
                this.loading = false;
                return;
            }

            if ( !parsedGeoJSON ) {
                this.geoJSONError = 'Failed to parse JSON as Geo JSON';
                this.loading = false;
                return;
            }

            if ( this.$props.allowedTypes.length > 0 && !this.$props.allowedTypes.includes( parsedGeoJSON.type ) ) {
                this.geoJSONError = `Geo Json must be one of the allowed types: ${this.$props.allowedTypes.join( ',' )}`;
                this.loading = false;
                return;
            }

            this.draw.deleteAll();

            this.draw.add( parsedGeoJSON );

            this.feature = this.draw.getAll();
            
            this.hadChangesMade = true;
            this.showGeoJSONUpload = false;
            this.geoJSONInput = null;
            this.disableWKT = false;
            this.disableGeoJSON = false;
            this.loading = false;

            this.map.once( 'idle', () => {
                this.updateBBOX();

                const center = getBBOXCenter( this.bbox );

                this.updateCenterMarker( center.lat, center.lon );

                this.emit();

                this.focus();
            } );
        },
        uploadWKT() {
            this.loading = true;
            this.wktError = null;

            const wktStr = ( this.wktInput || '' ).trim();

            if ( wktStr.length === 0 ) {
                this.wktError = 'Enter a valid WKT string';
                this.loading = false;
                return;
            }

            let parsedWKT;

            try {
                parsedWKT = wkt.parse( wktStr );
            } catch ( err ) {
                this.wktError = 'Failed to parse WKT string';
                this.loading = false;
                return;
            }

            if ( !parsedWKT ) {
                this.wktError = 'Failed to parse WKT string';
                this.loading = false;
                return;
            }

            if ( parsedWKT.type !== 'Polygon' && parsedWKT.type !== 'MultiPolygon' ) {
                this.wktError = 'Upload a WKT Polygon or MultiPolygon';
                this.loading = false;
                return;
            }

            const wktAsJSON = JSON.parse( JSON.stringify( parsedWKT ) );

            this.draw.deleteAll();

            this.draw.add( wktAsJSON );

            this.feature = this.draw.getAll();

            this.hadChangesMade = true;
            this.showWKTUpload = false;
            this.wktInput = null;
            this.disableWKT = false;
            this.disableGeoJSON = false;
            this.loading = false;

            this.map.once( 'idle', () => {
                this.updateBBOX();

                const center = getBBOXCenter( this.bbox );

                this.updateCenterMarker( center.lat, center.lon );

                this.emit();

                this.focus();
            } );
        },
        viewGeoJSON() {
            this.showFeatureViewer = true;
            this.disableViewWKT = false;
            this.disableViewGeoJSON = true;
            this.viewType = 'geojson';
        },
        viewWKT() {
            this.showFeatureViewer = true;
            this.disableViewGeoJSON = false;
            this.disableViewWKT = true;
            this.viewType = 'wkt';
        },
        hideFeatureViewer() {
            this.showFeatureViewer = false;
            this.disableViewGeoJSON = false;
            this.disableViewWKT = false;
            this.viewType = '';
        },
        toggleSupplementaryLayer( id ) {
            const layerID = `s-${id}`;

            if ( !this.map.getLayer( layerID ) ) {
                return;
            }

            const visibility = this.map.getLayoutProperty( layerID, 'visibility' );

            if ( visibility === 'none' ) {
                this.map.setLayoutProperty(
                    `s-${id}`,
                    'visibility',
                    'visible',
                );

                this.supplementaryLayerOptions = this.supplementaryLayerOptions.map( o => {
                    if ( o.id !== id ) {
                        return o;
                    }
                    const updated = o;
                    o.visible = true;
                    return updated;
                } );
            } else {
                this.map.setLayoutProperty(
                    `s-${id}`,
                    'visibility',
                    'none',
                );

                this.supplementaryLayerOptions = this.supplementaryLayerOptions.map( o => {
                    if ( o.id !== id ) {
                        return o;
                    }
                    const updated = o;
                    o.visible = false;
                    return updated;
                } );
            }
        },
        addSupplementaryLayers( layers ) {
            layers.forEach( layer => {
                const id = `s-${layer.id}`;

                this.map.addSource( id, {
                    type: 'geojson',
                    data: layer.data,
                    tolerance: 0
                } );

                this.map.addLayer( {
                    id: id,
                    source: id,
                    type: 'line',
                    layout: {},
                    state: {},
                    paint: {
                        'line-color': '#d1041d',
                        'line-width': 1
                    }
                } );

                this.supplementaryLayerOptions.push( {
                    id: layer.id,
                    layerID: id,
                    sourceID: id,
                    visible: true
                } );
            } );
        },
        onGeoJSONInput() {
            if ( !this.geoJSONInput ) {
                return;
            }
            this.geoJSONInput = this.geoJSONInput.trim();
            this.geoJSONInput = this.geoJSONInput.replaceAll( /^var geom_[0-9_\s]*={1}/g, '' );
            this.geoJSONInput = this.geoJSONInput.trim();
        }
    },
    watch: {
        supplementaryLayers( newValue, oldValue ) {
            if ( oldValue && oldValue.length > 0 ) {
                oldValue.forEach( layer => {
                    if ( this.map.getLayer( `s-${layer.id}` ) ) {
                        this.map.removeLayer( `s-${layer.id}` );
                    }
                    if ( this.map.getSource( `s-${layer.id}` ) ) {
                        this.map.removeSource( `s-${layer.id}` );
                    }
                } );
                this.supplementaryLayerOptions = [];
            }
            this.addSupplementaryLayers( newValue );
        }
    },
    mounted() {
        this.initMap();
    },
    props: {
        disabled: {
            type: Boolean,
            required: false,
            default: false
        },
        initialLatitude: {
            type: Number,
            required: false,
            default: null
        },
        initialLongitude: {
            type: Number,
            required: false,
            default: null
        },
        initialGeometryAsGeoJson: {
            type: Object,
            required: false,
            default: null
        },
        initialGeometryAsWkt: {
            type: String,
            required: false,
            default: ''
        },
        allowedTypes: {
            type: Array,
            required: false,
            default: () => []
        },
        supplementaryLayers: {
            type: Array,
            required: false,
            default: () => []
        }
    }
};
</script>

<style>
  #map {
    width: 100%;
    height: 45vh;
  }

  #map.disabled {
    cursor: not-allowed !important;
    pointer-events: none !important;
  }

  #map.disabled * {
    cursor: not-allowed !important;
    pointer-events: none !important;
  }

  #extras {
    position: absolute;
    top: 10px;
    left: 10px;
  }
</style>



// WEBPACK FOOTER //
// src/components/locations/LocationMap.vue